import classnames from 'classnames'
import * as d3 from 'd3'
import * as React from 'react'
import { Component, RefObject, createRef } from 'react'

import { Icon } from '../../main/components/icon'
import { getValue, setValue } from '../../main/services/local-storage'
import { decodeQueryString } from '../queryString'
import { AdTag, getInvestingChannelPage } from '../shared/components/Ads'
import LazyTippy from '../shared/lazyTippy'
import BarChartBig from './barChart'

interface Bar {
  name: string
  value: number
}

interface BarChartProps {
  data: Bar[]
}

class BarChart extends Component<BarChartProps> {
  svgRef: RefObject<SVGSVGElement>

  constructor(props: BarChartProps) {
    super(props)
    this.svgRef = createRef()
  }

  componentDidMount() {
    this.renderChart()
  }

  componentDidUpdate() {
    this.renderChart()
  }

  renderChart() {
    const data = this.props.data

    const barWidth = 3
    const width = data.length * (barWidth + 1)
    const height = 16

    const x = d3
      .scaleBand()
      .domain(data.map((d) => d.name))
      .range([0, width])

    let min = d3.min(data, (d: Bar) => d.value)!
    if (min < 0) {
      min = d3.min(data, (d: Bar) => Math.min(d.value, -d.value))!
    } else {
      min = 0
    }

    const y = d3
      .scaleLinear()
      .range([height, 0])
      .domain([min, d3.max(data, (d: Bar) => Math.abs(d.value))] as [number, number])
      .nice()
      .unknown(height / 2)

    const svg = d3.select(this.svgRef.current).property('value', [])

    let bars = svg.selectAll('.bar').data(data)
    bars
      .enter()
      .append('rect')
      .attr('class', function (d: Bar) {
        return 'bar bar--' + (d.value < 0 ? 'negative' : 'positive')
      })
      .attr('x', function (d: Bar) {
        return x(d.name)!
      })
      .attr('y', function (d: Bar) {
        return y(Math.max(0, d.value))
      })
      .attr('height', function (d: Bar) {
        let height = Math.abs(y(d.value) - y(0))
        if (d.value > 0 || d.value < 0) {
          height = Math.max(height, 0.5)
        }
        return height
      })
      .attr('width', barWidth)
    bars
      .transition()
      .attr('class', function (d: Bar) {
        return 'bar bar--' + (d.value < 0 ? 'negative' : 'positive')
      })
      .attr('x', function (d: Bar) {
        return x(d.name)!
      })
      .attr('y', function (d: Bar) {
        return y(Math.max(0, d.value))
      })
      .attr('height', function (d: Bar) {
        let height = Math.abs(y(d.value) - y(0))
        if (d.value > 0 || d.value < 0) {
          height = Math.max(height, 0.5)
        }
        return height
      })
      .attr('width', barWidth)
    bars.exit().remove()

    this.svgRef.current!.style.width = width + 'px'
    this.svgRef.current!.style.height = height + 'px'
  }

  render() {
    return <svg ref={this.svgRef} className="quote-bar-chart"></svg>
  }
}

function EliteHoverAd() {
  return (
    <div className="quote-statements-elite-tooltip">
      <div className="quote-statements-elite-tooltip_wrapper">
        <Icon name="lock" width="24" className="mx-auto text-blue-500" />
        <h2 className="quote-statements-elite-subscribe_title">Upgrade your FINVIZ experience</h2>
        <p>
          Join thousands of traders who make more informed decisions with&nbsp;our&nbsp;premium features. Real-time
          quotes, advanced&nbsp;visualizations, historical statements, and much more.
        </p>
        <a
          href="/elite.ashx?utm_source=finviz&utm_medium=banner&utm_campaign=quote-statements"
          target="_blank"
          className="tab-link"
        >
          Learn more
        </a>
      </div>
    </div>
  )
}

interface BarChartWrapperProps {
  data: Bar[]
}

class BarChartWrapper extends Component<BarChartWrapperProps> {
  state = {
    hover: false,
  }

  render() {
    return (
      <div className="tooltip">
        <LazyTippy
          placement="right"
          interactive={true}
          disabled={!this.props.data.some((x) => x.value !== null)}
          render={(attrs) => (
            <div
              className={classnames('statements-table_tooltiptext quote_statements-tooltip', {
                'is-ad': !FinvizSettings.hasUserPremium,
              })}
              {...attrs}
            >
              {FinvizSettings.hasUserPremium ? (
                <BarChartBig data={this.props.data} xAxisRotate={true} />
              ) : (
                <EliteHoverAd />
              )}
            </div>
          )}
        >
          <div>
            <BarChart data={this.props.data} />
          </div>
        </LazyTippy>
      </div>
    )
  }
}

interface StatementsState {
  source: 'F' | 'R'
  sheet: string
  timeframe: string
  currency: string | null
  data: Record<string, string[]> | null
}

const DEFAULT_SOURCE = 'F'

class Statements extends Component<any, StatementsState> {
  declare pageRef: ReturnType<typeof getInvestingChannelPage>

  state: StatementsState = {
    source: this._getDefaultSource(),
    sheet: 'I',
    timeframe: 'A',
    currency: null,
    data: null,
  }

  componentDidMount() {
    this._fetch()
    this.pageRef = getInvestingChannelPage()
    this.pageRef.create()
  }

  componentWillUnmount() {
    this.pageRef?.destroy()
  }

  render() {
    const { data } = this.state

    if (data === null) {
      // || (data && data.error)) {
      return null
    }

    const hasData = !data.error
    const hasRedesignEnabled = FinvizSettings?.hasRedesignEnabled

    return (
      <>
        <table
          width="100%"
          cellPadding="3"
          cellSpacing="0"
          // @ts-ignore
          border="0"
          className="statements-table-header"
        >
          <tbody>
            <tr>
              <td style={FinvizSettings.hasUserPremium ? { paddingLeft: 0 } : {}}>
                {FinvizSettings.hasUserPremium && (
                  // TODO fix classname when css mixins removal is merged
                  <select
                    className={classnames({
                      'myaccount-edit w-auto': !hasRedesignEnabled,
                      'fv-select': hasRedesignEnabled,
                    })}
                    value={this.state.source}
                    onChange={(e) => {
                      e.preventDefault()
                      const source = e.target.value === 'R' ? 'R' : 'F'
                      this.setState(
                        {
                          source: source,
                        },
                        () => {
                          this._fetch()
                        }
                      )
                      setValue('statements-source', source, DEFAULT_SOURCE, false)
                    }}
                  >
                    <option value="F">FactSet</option>
                    <option value="R">Reuters</option>
                  </select>
                )}
                {FinvizSettings.hasUserPremium && <>&nbsp;&nbsp;&nbsp;</>}

                <span className="fullview-links">
                  {this._renderSheetLink('I', 'income statement')} | {this._renderSheetLink('B', 'balance sheet')} |{' '}
                  {this._renderSheetLink('C', 'cash flow')}
                </span>
              </td>
              <td align="right">
                <span className="fullview-links">
                  {this._renderTimeframeLink('A', 'annual')} | {this._renderTimeframeLink('Q', 'quarterly')}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        {hasData && <StatementsTable {...this.state} />}
        {!hasData && (
          <table
            width="100%"
            cellPadding="3"
            cellSpacing="0"
            // @ts-ignore
            border="0"
            className={classnames({
              'styled-table-new is-rounded quote_statements-table is-tabular-nums': hasRedesignEnabled,
              'snapshot-table2 has-hovers statements-table': !hasRedesignEnabled,
              'is-free': !FinvizSettings.hasUserPremium,
            })}
          >
            <tbody>
              <tr className="styled-row is-hoverable is-bordered is-rounded is-border-top is-hover-borders">
                <td className={hasRedesignEnabled ? '' : 'snapshot-td2'}>
                  The statement is not available. Try switching between FactSet-Reuters source.
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </>
    )
  }

  _renderSheetLink(id: string, text: string) {
    const { sheet } = this.state
    return (
      <a
        href="#"
        className={classnames('tab-link', { 'font-bold': sheet === id })}
        onClick={(e) => {
          e.preventDefault()
          this.setState(
            {
              sheet: id,
            },
            () => {
              this._fetch()
            }
          )
        }}
      >
        {text}
      </a>
    )
  }

  _renderTimeframeLink(id: string, text: string) {
    const { timeframe } = this.state
    return (
      <a
        href="#"
        className={classnames('tab-link', { 'font-bold': timeframe === id })}
        onClick={(e) => {
          e.preventDefault()
          this.setState(
            {
              timeframe: id,
            },
            () => {
              this._fetch()
            }
          )
        }}
      >
        {text}
      </a>
    )
  }

  async _fetch() {
    const { source, sheet, timeframe } = this.state
    const { t } = decodeQueryString()

    try {
      const response = await fetch(`/api/statement.ashx?t=${t}&so=${source}&s=${sheet}${timeframe}`)
      const json = await response.json()

      this.setState({
        currency: json.currency,
        data: json.error ? json : json.data,
      })
    } catch {}
  }

  _getDefaultSource(): 'F' | 'R' {
    if (!FinvizSettings.hasUserPremium) {
      return DEFAULT_SOURCE
    }

    return getValue<string>('statements-source', DEFAULT_SOURCE) === 'R' ? 'R' : 'F'
  }
}

function StatementsTable({ data, currency }: StatementsState) {
  if (!data) return null

  function parseData(data: Record<string, string[]>, skipFirst: boolean) {
    let parsed: any = {}
    Object.keys(data).forEach((key) => {
      parsed[key] = data[key]
        .map((value, index) => {
          if (isChartable(key)) {
            let parsedValue
            if (value === '') {
              parsedValue = null
            } else {
              parsedValue = parseFloat(value.replace(/,/g, ''))
            }
            return {
              name: data['Period End Date'][index],
              value: parsedValue,
            }
          }
          return null
        })
        .slice(skipFirst ? 1 : 0)
    })

    return parsed as Record<string, Array<{ name: string; value: number }>>
  }

  function isChartable(label: string) {
    switch (label) {
      case 'Period End Date':
      case 'Period Length':
        return false
    }

    return true
  }

  function isHighlightRow(label: string) {
    switch (label) {
      case 'Period End Date':
      case 'Total Revenue':
      case 'Total Premiums Earned':
      case 'Interest Income':
      case 'Gross Profit':
      case 'Total Operating Expense':
      case 'Operating Income':
      case 'Net Income Before Taxes':
      case 'Net Income':
      case 'Total Current Assets':
      case 'Total Assets':
      case 'Total Current Liabilities':
      case 'Total Liabilities':
      case 'Total Equity':
      case 'Total Liabilities and Equity':
      case 'Cash from Operating Activities':
      case 'Cash from Investing Activities':
      case 'Cash from Financing Activities':
      case 'Net Change in Cash':
      case 'Price To Earnings Ratio':
      case 'Price to Book Ratio':
      case 'Price to Free Cash Flow':
        return true
    }

    return false
  }

  const hasRedesignEnabled = FinvizSettings?.hasRedesignEnabled
  const cellClass = hasRedesignEnabled ? '' : 'snapshot-td2'

  const skipFirst = data['Period End Date'][0] === 'TTM'
  const parsedData = parseData(data, skipFirst)
  const adRowIndexStart = !hasRedesignEnabled && data['Period Length'] ? 2 : 1

  return (
    <>
      <table
        width="100%"
        cellPadding="3"
        cellSpacing="0"
        // @ts-ignore
        border="0"
        className={classnames({
          'styled-table-new is-rounded quote_statements-table is-tabular-nums': hasRedesignEnabled,
          'snapshot-table2 has-hovers statements-table': !hasRedesignEnabled,
          'is-free': !FinvizSettings.hasUserPremium,
        })}
      >
        <tbody>
          {Object.keys(data).map((key, rowIndex) => (
            <tr
              key={key}
              className={classnames({
                'styled-row is-hoverable is-bordered is-rounded is-border-top is-hover-borders': hasRedesignEnabled,
                'is-highlighted': hasRedesignEnabled && isHighlightRow(key),
                'table-dark-row': !hasRedesignEnabled && isHighlightRow(key),
                'table-light-row': !hasRedesignEnabled && !isHighlightRow(key),
                'first-row': rowIndex === 0,
                'elite-row': rowIndex === adRowIndexStart,
              })}
            >
              <td className={cellClass}>{key}</td>
              <td className={cellClass} align="center">
                {isChartable(key) && <BarChartWrapper data={parsedData[key].reverse()} />}
              </td>
              {data[key].map((value, index) => {
                if (!FinvizSettings.hasUserPremium) {
                  if (rowIndex === adRowIndexStart && index === 3) {
                    return (
                      <td
                        key={index}
                        align="right"
                        className="quote-statements-elite-subscribe"
                        rowSpan={Object.keys(data).length - 1}
                        colSpan={data[key].length - 3}
                      >
                        <AdTag
                          name="IC_Statements"
                          className="mx-auto -mt-32 mb-15 text-center text-2xs text-blue-500"
                        />
                        <Icon name="lock" width="24" className="mx-auto text-blue-500" />
                        <h2 className="quote-statements-elite-subscribe_title">Upgrade your FINVIZ experience</h2>
                        <p className="quote-statements-elite-subscribe_text">
                          Join thousands of traders who make more informed decisions with&nbsp;our&nbsp;premium
                          features. Real-time quotes, advanced&nbsp;visualizations, historical statements, and much
                          more.
                        </p>
                        <a
                          href="/elite.ashx?utm_source=finviz&utm_medium=banner&utm_campaign=quote-statements"
                          target="_blank"
                          className="tab-link"
                        >
                          Learn more
                        </a>
                      </td>
                    )
                  }
                  if (
                    index > 2 &&
                    ((hasRedesignEnabled && key !== 'Period End Date') ||
                      (key !== 'Period End Date' && key !== 'Period Length'))
                  ) {
                    return null
                  }
                }

                return (
                  <td key={index} align="right" className={cellClass}>
                    {value}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <table
        className="quote_statements-footer"
        width="100%"
        cellPadding="3"
        cellSpacing="0"
        // @ts-ignore
        border="0"
      >
        <tbody>
          <tr>
            <td align="right">values in Millions, {currency}</td>
          </tr>
        </tbody>
      </table>
    </>
  )
}

export default Statements
